import { useState, useEffect } from 'react'
import {Calendar, Clock, MapPin, Video, Church, Users, ExternalLink, Filter, Repeat} from 'lucide-react'
import { motion } from 'framer-motion'
import { lumi } from '../lib/lumi'

interface ProgramEvent {
  _id: string
  title: string
  description: string
  type: 'LIVE' | 'CULTO' | 'ATIVIDADE'
  date: string
  time: string
  location?: string
  liveUrl?: string
  imageUrl?: string
  status: 'ACTIVE' | 'COMPLETED'
  createdAt: string
  recurrence?: 'none' | 'weekly'
  recurrenceDays?: number[]
}

const Programacao = () => {
  const [events, setEvents] = useState<ProgramEvent[]>([])
  const [loading, setLoading] = useState(true)
  const [filterType, setFilterType] = useState<string>('ALL')

  useEffect(() => {
    fetchEvents()
  }, [])

  const fetchEvents = async () => {
    try {
      setLoading(true)
      // Busca todos os registros ativos; eventos recorrentes têm date:'' então
      // não podemos ordenar por date no banco — ordenamos no frontend
      const { list } = await lumi.entities.kids_comics.list({
        filter: { $or: [{ status: 'ACTIVE' }, { status: { $exists: false } }] },
        sort: { createdAt: -1 },
        limit: 200
      })
      // Filtra apenas ativos (cobre registros sem campo status)
      const active = (list as ProgramEvent[]).filter(
        (e) => !e.status || e.status === 'ACTIVE'
      )
      // Ordena: recorrentes primeiro (sem data), depois pontuais por data asc
      const sorted = [
        ...active.filter(e => e.recurrence === 'weekly'),
        ...active
          .filter(e => e.recurrence !== 'weekly')
          .sort((a, b) => {
            if (!a.date) return 1
            if (!b.date) return -1
            return new Date(a.date).getTime() - new Date(b.date).getTime()
          })
      ]
      setEvents(sorted)
    } catch (error) {
      console.error('Erro ao carregar programação:', error)
    } finally {
      setLoading(false)
    }
  }

  const filteredEvents = filterType === 'ALL' 
    ? events 
    : events.filter(event => event.type === filterType)

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'LIVE': return <Video size={20} />
      case 'CULTO': return <Church size={20} />
      case 'ATIVIDADE': return <Users size={20} />
      default: return <Calendar size={20} />
    }
  }

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'LIVE': return 'from-red-500 to-pink-500'
      case 'CULTO': return 'from-blue-600 to-indigo-600'
      case 'ATIVIDADE': return 'from-green-500 to-teal-500'
      default: return 'from-gray-600 to-gray-700'
    }
  }

  const getTypeBadgeColor = (type: string) => {
    switch (type) {
      case 'LIVE': return 'bg-red-100 text-red-700'
      case 'CULTO': return 'bg-blue-100 text-blue-700'
      case 'ATIVIDADE': return 'bg-green-100 text-green-700'
      default: return 'bg-gray-100 text-gray-700'
    }
  }

  const WEEK_DAYS_FULL = ['Domingo', 'Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta', 'Sábado']

  const formatRecurrenceDays = (days: number[]): string => {
    if (!days || days.length === 0) return ''
    const sorted = [...days].sort((a, b) => a - b)
    if (sorted.length === 7) return 'Todos os dias'
    if (sorted.length === 1) return `Toda ${WEEK_DAYS_FULL[sorted[0]]}`
    return sorted.map(d => WEEK_DAYS_FULL[d]).join(', ')
  }

  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    return date.toLocaleDateString('pt-BR', { 
      day: '2-digit', 
      month: 'long', 
      year: 'numeric' 
    })
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-blue-50">
      {/* Header Premium */}
      <section className="relative bg-gradient-to-r from-[#1e3a5f] via-[#2d5a7b] to-[#1e3a5f] py-20 overflow-hidden">
        <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1438232992991-995b7058bbb3?w=1920')] bg-cover bg-center opacity-10"></div>
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#1e3a5f]/50 to-[#1e3a5f]"></div>
        
        <div className="relative max-w-6xl mx-auto px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <div className="flex justify-center mb-6">
              <div className="bg-white/10 backdrop-blur-md rounded-full p-4 border border-white/20">
                <Calendar className="text-white" size={48} />
              </div>
            </div>
            <h1 className="text-5xl md:text-6xl font-serif font-bold text-white mb-4 drop-shadow-2xl">
              Programação REFUGE IN CHRIST MINISTRY
            </h1>
            <p className="text-xl md:text-2xl text-white/90 font-light max-w-3xl mx-auto">
              Acompanhe nossas lives, cultos e atividades
            </p>
          </motion.div>
        </div>
      </section>

      {/* Filtros */}
      <section className="py-8 bg-white border-b">
        <div className="max-w-6xl mx-auto px-4">
          <div className="flex flex-wrap gap-3 justify-center">
            <button
              onClick={() => setFilterType('ALL')}
              className={`flex items-center gap-2 px-6 py-3 rounded-full font-semibold transition-all ${
                filterType === 'ALL'
                  ? 'bg-[#1e3a5f] text-white shadow-lg scale-105'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <Filter size={18} />
              Todos
            </button>
            <button
              onClick={() => setFilterType('LIVE')}
              className={`flex items-center gap-2 px-6 py-3 rounded-full font-semibold transition-all ${
                filterType === 'LIVE'
                  ? 'bg-gradient-to-r from-red-500 to-pink-500 text-white shadow-lg scale-105'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <Video size={18} />
              Lives
            </button>
            <button
              onClick={() => setFilterType('CULTO')}
              className={`flex items-center gap-2 px-6 py-3 rounded-full font-semibold transition-all ${
                filterType === 'CULTO'
                  ? 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-lg scale-105'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <Church size={18} />
              Cultos
            </button>
            <button
              onClick={() => setFilterType('ATIVIDADE')}
              className={`flex items-center gap-2 px-6 py-3 rounded-full font-semibold transition-all ${
                filterType === 'ATIVIDADE'
                  ? 'bg-gradient-to-r from-green-500 to-teal-500 text-white shadow-lg scale-105'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <Users size={18} />
              Atividades
            </button>
          </div>
        </div>
      </section>

      {/* Lista de Eventos */}
      <section className="py-16">
        <div className="max-w-6xl mx-auto px-4">
          {loading ? (
            <div className="text-center py-20">
              <div className="inline-block animate-spin rounded-full h-16 w-16 border-4 border-[#1e3a5f] border-t-transparent"></div>
              <p className="text-gray-600 mt-4">Carregando programação...</p>
            </div>
          ) : filteredEvents.length === 0 ? (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white rounded-2xl shadow-lg p-12 text-center"
            >
              <Calendar className="mx-auto text-gray-300 mb-4" size={64} />
              <h3 className="text-2xl font-bold text-gray-700 mb-2">
                Nenhum evento agendado
              </h3>
              <p className="text-gray-500">
                {filterType === 'ALL' 
                  ? 'Em breve teremos novidades na programação'
                  : `Nenhum(a) ${filterType.toLowerCase()} agendado(a) no momento`}
              </p>
            </motion.div>
          ) : (
            <div className="space-y-6">
              {filteredEvents.map((event, index) => (
                <motion.div
                  key={event._id}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-2xl transition-all duration-300 group"
                >
                  <div className="flex flex-col md:flex-row">
                    {/* Imagem */}
                    <div className="md:w-1/3 relative overflow-hidden">
                      {event.imageUrl ? (
                        <img
                          src={event.imageUrl}
                          alt={event.title}
                          className="w-full h-64 md:h-full object-cover group-hover:scale-110 transition-transform duration-500"
                        />
                      ) : (
                        <div className={`w-full h-64 md:h-full bg-gradient-to-br ${getTypeColor(event.type)} flex items-center justify-center`}>
                          <div className="text-white">
                            {getTypeIcon(event.type)}
                          </div>
                        </div>
                      )}
                      <div className="absolute top-4 left-4 flex flex-col gap-2">
                        <span className={`${getTypeBadgeColor(event.type)} px-4 py-2 rounded-full text-sm font-bold shadow-lg flex items-center gap-2`}>
                          {getTypeIcon(event.type)}
                          {event.type}
                        </span>
                        {event.recurrence === 'weekly' && (
                          <span className="bg-amber-500 text-white px-3 py-1 rounded-full text-xs font-bold shadow-md flex items-center gap-1">
                            <Repeat size={12} />
                            Evento Fixo
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Conteúdo */}
                    <div className="md:w-2/3 p-6 md:p-8 flex flex-col justify-between">
                      <div>
                        <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3 group-hover:text-[#1e3a5f] transition-colors">
                          {event.title}
                        </h3>
                        <p className="text-gray-600 mb-4 leading-relaxed">
                          {event.description}
                        </p>
                        <div className="flex flex-wrap gap-4 mb-4">
                          <div className="flex items-center gap-2 text-gray-700">
                            {event.recurrence === 'weekly' ? (
                              <Repeat size={18} className="text-amber-500" />
                            ) : (
                              <Calendar size={18} className="text-[#6b8f71]" />
                            )}
                            {event.recurrence === 'weekly' ? (
                              <span className="font-semibold text-amber-700">
                                {formatRecurrenceDays(event.recurrenceDays || [])}
                              </span>
                            ) : (
                              <span className="font-medium">{event.date ? formatDate(event.date) : '—'}</span>
                            )}
                          </div>
                          <div className="flex items-center gap-2 text-gray-700">
                            <Clock size={18} className="text-[#6b8f71]" />
                            <span className="font-medium">{event.time}</span>
                          </div>
                          {event.location && (
                            <div className="flex items-center gap-2 text-gray-700">
                              <MapPin size={18} className="text-[#6b8f71]" />
                              <span className="font-medium">{event.location}</span>
                            </div>
                          )}
                        </div>
                      </div>
                      {event.liveUrl && (
                        <div className="mt-4">
                          <a
                            href={event.liveUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={`inline-flex items-center gap-2 bg-gradient-to-r ${getTypeColor(event.type)} text-white px-6 py-3 rounded-full font-bold hover:shadow-xl hover:scale-105 transition-all`}
                          >
                            <ExternalLink size={20} />
                            {event.type === 'LIVE' ? 'Assistir Live' : 'Mais Informações'}
                          </a>
                        </div>
                      )}
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Chamada para Ação */}
      <section className="py-16 bg-gradient-to-r from-[#1e3a5f] via-[#2d5a7b] to-[#1e3a5f] relative overflow-hidden">
        <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1511632765486-a01980e01a18?w=1920')] bg-cover bg-center opacity-5"></div>
        <div className="relative max-w-4xl mx-auto px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <div className="bg-white/10 backdrop-blur-md rounded-full p-4 inline-block mb-6 border border-white/20">
              <Church className="text-white" size={48} />
            </div>
            <h3 className="text-4xl font-serif font-bold text-white mb-4 drop-shadow-2xl">
              Junte-se a Nós!
            </h3>
            <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
              Venha fazer parte da nossa comunidade de fé. Você é sempre bem-vindo(a) em nossos cultos e atividades.
            </p>
            <a
              href="/contact"
              className="inline-flex items-center gap-2 bg-gradient-to-r from-[#d4af37] to-[#f4d03f] text-[#1e3a5f] px-8 py-4 rounded-full font-bold text-lg hover:shadow-2xl hover:scale-105 transition-all"
            >
              <Users size={24} />
              Entre em Contato
            </a>
          </motion.div>
        </div>
      </section>
    </div>
  )
}

export default Programacao
